import { noop } from 'lodash';
import { Fragment, useEffect, useRef, useState } from 'react';

import { reactIcons as Icon } from '@project44/manifest-ds';
import { BoxProps } from '@project44/manifest-ui-deprecated';

import { EntityEnum } from '../../definitions/enums/Entity';
import { StyledTag } from '../StyledTag';

import {
  PanelExtraContent,
  PanelHeaderContent,
  StyledIconButton,
  StyledPanel,
} from './styles';

interface PanelProps extends BoxProps {
  children?: JSX.Element;
  entityType: EntityEnum;
  expandable?: boolean;
  closable?: boolean;
  expandWidth?: number;
  extra?: JSX.Element | JSX.Element[];
  title?: string;
  panelItemsCount?: number;
  onExpand?: () => void;
  onCollapse?: () => void;
  onClose?: () => void;
}

export const Panel = ({
  children,
  entityType,
  expandable = false,
  closable = false,
  expandWidth,
  extra,
  sx,
  title,
  panelItemsCount,
  onExpand,
  onCollapse,
  onClose,
  ...boxProps
}: PanelProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  useEffect(() => {
    if (expandable && isExpanded) {
      const child = ref.current;
      const resizeObserver = new ResizeObserver(() => {
        child?.scrollIntoView?.({ inline: 'end' });
      });
      if (child) resizeObserver.observe(child);
      return () => {
        if (child) resizeObserver.unobserve(child);
      };
    }
  }, [expandable, isExpanded]);

  const handleExpansion = () => {
    setIsExpanded((isExpanded) => {
      if (isExpanded && onCollapse) onCollapse();
      if (!isExpanded && onExpand) onExpand();
      return !isExpanded;
    });
  };

  return (
    <StyledPanel
      data-testid="panel"
      expand={isExpanded ? 1 : 0}
      expandwidth={expandWidth}
      sx={sx}
      {...boxProps}
      ref={ref}
    >
      {(title || extra || expandable || closable) && (
        <PanelHeaderContent data-testid="panel-header" entityType={entityType}>
          {title}{' '}
          {panelItemsCount !== undefined && (
            <StyledTag>{panelItemsCount}</StyledTag>
          )}
          <PanelExtraContent data-testid="panel-extra">
            <Fragment key="extra">{extra}</Fragment>
            {expandable ? (
              <StyledIconButton
                size="small"
                variant="tertiary"
                data-testid="panel-expand-collapse"
                onClick={handleExpansion}
              >
                {isExpanded ? (
                  <Icon.Shrink data-testid="panel-expand-shrink" size="small" />
                ) : (
                  <Icon.Grow data-testid="panel-expand-grow" size="small" />
                )}
              </StyledIconButton>
            ) : null}
            {closable ? (
              <StyledIconButton
                size="small"
                variant="tertiary"
                data-testid="close-button"
                onClick={onClose || noop}
              >
                <Icon.Close size="small" />
              </StyledIconButton>
            ) : null}
          </PanelExtraContent>
        </PanelHeaderContent>
      )}
      {children}
    </StyledPanel>
  );
};
